import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ReactSVG } from 'react-svg';
import { Box, styled, useTheme } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { IconButton, Tooltip, Typography } from '@zendesk/forethought-elements';
import useIsDeflectionInsightsEnabled from '../intent-conversation-analytics/hooks/useIsDeflectionInsightsEnabled';
import moreIcon from '../workflow-builder-edit/assets/icons/more-icon.svg';
import moreIconWhite from '../workflow-builder-edit/assets/icons/more-icon-white.svg';
import { useBuildInsightsChatNavigationCallback } from '../workflow-builder-edit/hooks';
import { useSolveMetricsQueryParams } from './intent-workflows-table/hooks/useSolveMetricsQueryParams';
import { dateRangeToTimestamp } from 'src/components/dashboard-pages/solve-insights/helpers';
import { useChannelAccess } from 'src/hooks/useChannelAccess';
import { useIsAbTestAvailable } from 'src/hooks/useIsAbTestAvailable';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import {
  SubflowType,
  WorkflowBuilderHeaderChannel,
} from 'src/types/workflowBuilderAPITypes';
import {
  meetsLevel,
  TAB_CHANNEL_TO_SOLVE_CHANNEL,
} from 'src/utils/channelAccess';
import { Routes } from 'src/utils/enums';
import { last30DaysTimeRange } from 'src/utils/timeRangeHelpers';

interface WorkflowDropdownMenuProps {
  abTestDisabled?: boolean;
  abTestDisabledReason?: string;
  deleteDisabledTooltip?: string;
  intentId: string;
  intentName: string;
  isCommonIntentWorkflowType: boolean;
  isDarkMode?: boolean;
  isDefaultHandoff: boolean;
  isDisabled?: boolean;
  isHandoff?: boolean;
  /**
   * True when this menu is rendered in the Assist/Playbook channel. Used to
   * hide AB-testing options — belt-and-suspenders with `useIsAbTestAvailable`,
   * which gates on the `/assist/*` route. The legacy `?channel=playbook` URL
   * outside `/assist/*` can still render this menu, so the explicit channel
   * check covers that case.
   */
  isPlaybookChannel?: boolean;
  onCreateAbTestClicked?: () => void;
  onDeleteClicked: () => void;
  onDuplicateClicked?: () => void;
  onEditClicked: () => void;
  onPreviewClicked?: () => void;
  onViewInsightClick: () => void;
  shouldEnableDelete: boolean;
  shouldEnableViewInsight: boolean;
  shouldShowChats: boolean;
  shouldShowDuplicate: boolean;
  shouldShowEdit: boolean;
  shouldShowPreview: boolean;
  subflowType?: SubflowType;
  workflowId?: string;
}

export const WorkflowDropdownMenu: React.FC<
  React.PropsWithChildren<WorkflowDropdownMenuProps>
> = ({
  abTestDisabled = false,
  abTestDisabledReason,
  deleteDisabledTooltip,
  intentId,
  intentName,
  isCommonIntentWorkflowType,
  isDarkMode = false,
  isDefaultHandoff,
  isDisabled = false,
  isHandoff = false,
  isPlaybookChannel = false,
  onCreateAbTestClicked,
  onDeleteClicked,
  onDuplicateClicked,
  onEditClicked,
  onPreviewClicked,
  onViewInsightClick,
  shouldEnableDelete,
  shouldEnableViewInsight,
  shouldShowChats,
  shouldShowDuplicate,
  shouldShowEdit,
  shouldShowPreview,
  subflowType,
  workflowId,
}) => {
  const { t } = useTranslation('workflow-builder');
  const isVoiceOutbound = subflowType === 'voice_outbound';
  const duplicateLabel = isVoiceOutbound
    ? t('dropdownMenu.duplicateAgent')
    : t('dropdownMenu.duplicateIntent');
  const idLabel = isVoiceOutbound
    ? t('dropdownMenu.agentId')
    : t('dropdownMenu.intentId');
  const idCopyValue = isVoiceOutbound ? (workflowId ?? '') : intentId;
  const idToastTitle = isVoiceOutbound
    ? t('dropdownMenu.agentIdCopied')
    : t('dropdownMenu.intentIdCopied');
  const idTooltipContent = isVoiceOutbound
    ? t('dropdownMenu.copyAgentId')
    : t('dropdownMenu.copyIntentId');
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const isDeflectionInsightsEnabled = useIsDeflectionInsightsEnabled();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { channel } = useSolveMetricsQueryParams();
  const isAbTestAvailable = useIsAbTestAvailable();
  const { hasGrants, levelFor } = useChannelAccess();
  const channelLevel = levelFor(TAB_CHANNEL_TO_SOLVE_CHANNEL[channel]);
  const canEdit = !hasGrants || meetsLevel(channelLevel, 'edit');
  const canDelete = !hasGrants || meetsLevel(channelLevel, 'publish');
  const noEditTooltip = t('dropdownMenu.noEditPermission');

  const navigateToInsightsChat =
    useBuildInsightsChatNavigationCallback(intentId);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChatNavigation = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    if (channel === 'widget') {
      navigateToInsightsChat();
      return;
    }

    const header: WorkflowBuilderHeaderChannel = 'chats';
    const currentQuery = new URLSearchParams(search);
    const defaultTimestamp = dateRangeToTimestamp(last30DaysTimeRange);
    const startTime =
      currentQuery.get('start') || defaultTimestamp.start_timestamp.toString();
    const endTime =
      currentQuery.get('end') || defaultTimestamp.end_timestamp.toString();

    const queryParams = new URLSearchParams({
      channel,
      end: endTime,
      header,
      start: startTime,
    });

    if (isDeflectionInsightsEnabled) {
      queryParams.set('filters', `intent_ids.${intentId}`);
    } else {
      queryParams.set('intentId', intentId);
    }

    navigate({
      pathname: Routes.WORKFLOW_BUILDER,
      search: '?' + queryParams.toString(),
    });
  };

  return (
    <>
      <IconButton
        aria-label={t('dropdownMenu.menuAriaLabel', { intentName })}
        disabled={isDisabled}
        onClick={handleClick}
        variant='ghost'
      >
        <ReactSVG src={isDarkMode ? moreIconWhite : moreIcon} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        onClose={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleClose();
        }}
        open={open}
        sx={{
          '& .MuiList-root > *:not(:last-child)': {
            marginBottom: '8px',
          },
          '& .MuiPaper-root': {
            border: `1px solid ${palette.colors.grey[200]}`,
            borderRadius: '4px',
            boxShadow:
              '0px 0px 1px rgba(0, 12, 32, 0.04), 2px 5px 8px rgba(3, 17, 38, 0.107135)',
            padding: '4px 0',
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        {shouldEnableViewInsight && (
          <StyledMenuItem
            aria-label={t('dropdownMenu.viewInsightAriaLabel')}
            disableRipple
            onClick={e => {
              e.stopPropagation();
              onViewInsightClick?.();
              handleClose();
            }}
          >
            <Typography variant='font14'>
              {t('dropdownMenu.viewInsights')}
            </Typography>
          </StyledMenuItem>
        )}
        {shouldShowPreview && (
          <StyledMenuItem
            aria-label={t('dropdownMenu.previewIntent')}
            disableRipple
            onClick={e => {
              e.stopPropagation();
              onPreviewClicked && onPreviewClicked();
              handleClose();
            }}
          >
            <Typography variant='font14'>
              {t('dropdownMenu.previewIntent')}
            </Typography>
          </StyledMenuItem>
        )}
        {isAbTestAvailable &&
          onCreateAbTestClicked &&
          !isHandoff &&
          !isDefaultHandoff &&
          !isCommonIntentWorkflowType &&
          !isPlaybookChannel && (
            <Tooltip
              tooltipContent={
                hasGrants
                  ? t('dropdownMenu.noAbTestPermission')
                  : abTestDisabled && abTestDisabledReason
                    ? abTestDisabledReason
                    : ''
              }
            >
              <StyledMenuItem
                aria-label={t('dropdownMenu.createAbTest')}
                disabled={abTestDisabled || hasGrants}
                disableRipple
                onClick={e => {
                  e.stopPropagation();
                  onCreateAbTestClicked();
                  handleClose();
                }}
              >
                <Typography variant='font14'>
                  {t('dropdownMenu.createAbTest')}
                </Typography>
              </StyledMenuItem>
            </Tooltip>
          )}
        {shouldShowEdit && (
          <Tooltip tooltipContent={canEdit ? '' : noEditTooltip}>
            <StyledMenuItem
              aria-label={t('dropdownMenu.editIntentAriaLabel')}
              disabled={!canEdit}
              disableRipple
              onClick={e => {
                e.stopPropagation();
                onEditClicked();
                handleClose();
              }}
            >
              <Typography variant='font14'>
                {isHandoff
                  ? t('dropdownMenu.edit')
                  : t('dropdownMenu.editIntent')}
              </Typography>
            </StyledMenuItem>
          </Tooltip>
        )}
        {shouldShowDuplicate && (
          <Tooltip tooltipContent={canEdit ? '' : noEditTooltip}>
            <StyledMenuItem
              aria-label={duplicateLabel}
              disabled={!canEdit}
              disableRipple
              onClick={e => {
                e.stopPropagation();
                onDuplicateClicked && onDuplicateClicked();
                handleClose();
              }}
            >
              <Typography variant='font14'>{duplicateLabel}</Typography>
            </StyledMenuItem>
          </Tooltip>
        )}
        {!!idCopyValue && (
          <StyledMenuItem
            aria-label={idTooltipContent}
            disableRipple
            onClick={e => {
              e.stopPropagation();
              navigator.clipboard.writeText(idCopyValue);
              dispatch(
                setGlobalToastOptions({
                  title: idToastTitle,
                  variant: 'main',
                }),
              );
              handleClose();
            }}
          >
            <Tooltip tooltipContent={idTooltipContent}>
              <Box
                sx={{
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Typography variant='font14'>{idLabel}</Typography>
              </Box>
            </Tooltip>
          </StyledMenuItem>
        )}
        {shouldShowChats && (
          <StyledMenuItem
            aria-label={t('dropdownMenu.viewChatsAriaLabel', { intentName })}
            disableRipple
            onClick={handleChatNavigation}
          >
            <Typography variant='font14'>{t('dropdownMenu.chats')}</Typography>
          </StyledMenuItem>
        )}
        <Tooltip
          tooltipContent={
            !canDelete
              ? t('dropdownMenu.noDeletePermission')
              : !shouldEnableDelete && deleteDisabledTooltip
                ? deleteDisabledTooltip
                : ''
          }
        >
          <StyledMenuItem
            aria-label={t('dropdownMenu.deleteAriaLabel', { intentName })}
            disabled={!shouldEnableDelete || !canDelete}
            disableRipple
            onClick={e => {
              e.stopPropagation();
              onDeleteClicked();
              handleClose();
            }}
          >
            <Typography color={palette.colors.red[500]} variant='font14'>
              {t('dropdownMenu.delete')}
            </Typography>
          </StyledMenuItem>
        </Tooltip>
      </Menu>
    </>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- TODO: fix later
export const StyledMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.palette.colors.primary[100]};
  }
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;
